import {
  MutateFunction,
  MutateOptions,
  QueryFunction,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { useContext, useEffect } from "react";
import { UserContext } from "../../../context/global/user-context";

type BlazeQueryResponse = {
  response_type:
    | "greeting"
    | "academic_within_scope"
    | "academic_out_of_scope"
    | "non_academic"
    | "learning_path"
    | "lecture_suggestion"
    | "live_classes_schedule"
    | "buddy_recommendation";
  answer?: string;
  hinglish_answer?: string;
  english_answer?: string;
  detected_subject?: string;
  detected_chapter?: string;
  detected_grade?: string;
  detected_language?: "english" | "hindi" | "hinglish";
  recommended_buddy?: string;
  message?: string;
  explanation?: string;
  suggestion?: string;
  switch_prompt?: string;
  query_classification?: string;
  current_buddy?: string;
  buddy_used?: string;
  resources?: Array<{
    title: string;
    url: string;
    type: string;
    description?: string;
    thumbnail?: string;
  }>;
  sources?: Array<{
    title: string;
    url: string;
    snippet?: string;
  }>;
  learning_path?: {
    introduction?: Array<{
      step: number;
      instruction: string;
      lecture_title: string;
      lecture_type: string;
      lecture_path?: string;
      why: string;
    }>;
    core_concepts?: Array<{
      step: number;
      instruction: string;
      lecture_title: string;
      lecture_type: string;
      lecture_path?: string;
      why: string;
    }>;
    application_examples?: Array<{
      step: number;
      instruction: string;
      lecture_title: string;
      lecture_type: string;
      lecture_path?: string;
      why: string;
    }>;
    practice_exercises?: Array<{
      step: number;
      instruction: string;
      lecture_title: string;
      lecture_type: string;
      lecture_path?: string;
      why: string;
    }>;
    review_consolidation?: Array<{
      step: number;
      instruction: string;
      lecture_title: string;
      lecture_type: string;
      lecture_path?: string;
      why: string;
    }>;
  };
  suggested_follow_ups?: string[];
  suggested_lectures?: Array<{
    id: string;
    title: string;
    path: string;
    youtube_url?: string;
    score?: number;
    type?: string;
  }>;
  live_classes?: Array<{
    id: string;
    title: string;
    subject: string;
    topic: string;
    instructor: string;
    start_time: string;
    duration: number;
    status: string;
    meeting_link: string;
  }>;
  metrics: {
    execution_time: number;
    chatgpt_prompt: string;
    chatgpt_model: string;
    syllabus: string;
    subject_name: string;
    chapter_name: string;
    grade: string;
    language: string;
  };
  conversation_history?: Array<{
    role: string;
    function_calls?: any;
    content: string;
  }>;
};

// New interface for interim message returned by search_relevant_lectures
type InterimMessageResponse = {
  message: string;
  topic_query_text: string;
  response_type: string;
};

type BlazeLectureRecommendationResponse = {
  id: string;
  intent: string;
  results: Array<{
    id: string;
    path: string;
    score: number;
    title: string;
    type: string;
    youtube_url: string;
  }>;
  metrics: {
    execution_time: number;
    query: string;
    intent: string;
    weights: Record<string, number>;
    sentence_transformer_model: string;
    elasticsearch_index: string;
    zero_shot_classification_model: string;
    elasticsearch_model: string;
  };
};

type ResponseWithMetrics<T> = {
  data: T;
  metrics: {
    response_time: number;
  };
};

type StreamCallbacks = {
  onInterimMessage: (message: InterimMessageResponse) => void;
  onChunkReceived: (chunk: Partial<BlazeQueryResponse>) => void;
  onComplete: (response: BlazeQueryResponse) => void;
  onError: (error: Error) => void;
};

type UseBlazeLectureRecommendationMutationOptions = {
  query: {
    question: string;
    answer: string;
  };
  signal?: AbortSignal;
};
export const useLectureRecommendationMutate = (
  options?: MutateOptions<
    ResponseWithMetrics<BlazeLectureRecommendationResponse>,
    Error,
    UseBlazeLectureRecommendationMutationOptions
  >
) => {
  const fetchAnswerFromQuery: MutateFunction<
    ResponseWithMetrics<BlazeLectureRecommendationResponse>,
    Error,
    UseBlazeLectureRecommendationMutationOptions
  > = async ({ query, signal }) => {
    const startTime = performance.now();
    const res = await fetch(
      "https://vector-search-python-fast-661886367826.asia-south1.run.app/search-query-fast",
      // "http://localhost:8080/search-query-fast",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: query.question,
          include_youtube_url: true,
        }),
        signal,
      }
    );
    const data = (await res.json()) as BlazeLectureRecommendationResponse;
    const endTime = performance.now();
    return {
      data,
      metrics: {
        response_time: endTime - startTime,
      },
    };
  };

  const _mutate = useMutation({
    mutationFn: fetchAnswerFromQuery,
    retry: 1,
    ...(options ?? {}),
  });

  return _mutate;
};

type UseBlazeQueryMutationOptions = {
  query: string;
  syllabus: string;
  grade: string;
  signal?: AbortSignal;
  conversation_history?: Array<{
    role: string;
    function_calls?: any;
    content: string;
  }>;
  streamCallbacks?: StreamCallbacks;
  user_lecture_history?: Array<any>;
  selected_buddy?: string | null;
};

// Utility functions for handling SSE JSON parsing
const jsonRepair = (str: string): string => {
  // Simple function to attempt to repair common JSON issues
  // Replace unescaped quotes within strings
  let result = str;

  // Fix trailing commas
  result = result.replace(/,\s*}/g, "}").replace(/,\s*\]/g, "]");

  // Fix missing quotes around property names
  result = result.replace(/([{,]\s*)([a-zA-Z0-9_$]+)(\s*:)/g, '$1"$2"$3');

  // Fix single quotes used instead of double quotes
  result = result.replace(/'/g, '"');

  return result;
};

export const useBlazeQueryMutate = (
  options?: MutateOptions<
    ResponseWithMetrics<BlazeQueryResponse>,
    Error,
    UseBlazeQueryMutationOptions
  >
) => {
  const [user] = useContext(UserContext).user;

  useEffect(() => {
    console.log("Test 02020 user1: ", user);
  }, [user]);

  const fetchAnswerFromQuery: MutateFunction<
    ResponseWithMetrics<BlazeQueryResponse>,
    Error,
    UseBlazeQueryMutationOptions
  > = async ({
    query,
    syllabus,
    grade,
    signal,
    conversation_history,
    streamCallbacks,
    user_lecture_history,
    selected_buddy,
  }) => {
    const startTime = performance.now();

    console.log("Test 02020 user2: ", user);

    // For non-streaming requests or when callbacks aren't provided, use regular fetch
    if (!streamCallbacks) {
      const res = await fetch("/api/gemini-buddy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query,
          syllabus,
          grade,
          conversation_history,
          user_lecture_history,
          userId: user?.uid === "guest-user" ? null : user?.uid,
          selected_buddy,
        }),
        signal,
      });

      const responseData = await res.json();

      if (res.status !== 200) {
        // Special handling for academic_out_of_scope and non_academic responses
        // These come with status 400 but have valid data we want to display
        if (res.status === 400 && responseData.question_answer) {
          const { question_answer } = responseData;

          // Return the data in the expected format so it can be displayed properly
          return {
            data: {
              response_type: question_answer.response_type,
              answer: question_answer.answer || responseData.error,
              hinglish_answer: question_answer.hinglish_answer,
              english_answer: question_answer.english_answer,
              detected_subject: question_answer.detected_subject,
              detected_chapter: question_answer.detected_chapter,
              detected_grade: question_answer.detected_grade,
              detected_language: question_answer.detected_language,
              metrics: {
                execution_time: 0, // Default as it might not be included
                chatgpt_prompt: query,
                chatgpt_model: "gemini-2.0-flash", // Update to Gemini model
                syllabus: syllabus,
                subject_name: question_answer.detected_subject || "General",
                chapter_name: question_answer.detected_chapter || "General",
                grade: grade,
                language: question_answer.detected_language || "english",
              },
            },
            metrics: {
              response_time: performance.now() - startTime,
            },
          };
        }

        // For other errors, throw as usual
        throw new Error(responseData.error || "An error occurred");
      }

      const data = responseData as BlazeQueryResponse;
      const endTime = performance.now();

      return {
        data,
        metrics: {
          response_time: endTime - startTime,
        },
      };
    }

    // Streaming implementation with EventSource/SSE
    return new Promise<ResponseWithMetrics<BlazeQueryResponse>>(
      (resolve, reject) => {
        // Use fetch with streaming support
        fetch("/api/gemini-buddy", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Transfer-Encoding": "chunked",
          },
          body: JSON.stringify({
            query,
            syllabus,
            grade,
            conversation_history,
            userId: user?.uid === "guest-user" ? null : user?.uid,
            stream: true, // Indicate we want streaming
            user_lecture_history, // Include user lecture history in streaming requests
            selected_buddy, // Pass the selected buddy in streaming requests
          }),
          signal,
        })
          .then(async (response) => {
            if (!response.ok) {
              console.log("Error in response: ", response);
              try {
                const errorData = await response.json();
                console.error("Error data from server:", errorData);

                // **Access your customized error information here**
                if (errorData && errorData.error) {
                  const errorMessage =
                    errorData?.question_answer?.answer || errorData.error;
                  console.log("Custom error message:", errorMessage);
                  // You can now pass this errorMessage to your error handling logic
                  // or the reject function if you want the promise to reject with this data.
                  reject(new Error(`${errorMessage}`));
                } else {
                  reject(new Error(`HTTP error ${response.status}`));
                }
              } catch (e) {
                // If parsing the JSON fails, still reject with the basic error
                reject(new Error(`HTTP error ${response.status}`));
              }
              return; // Important: Exit the .then block after handling the error
            }

            if (!response.body) {
              throw new Error("ReadableStream not supported");
            }

            // Final complete response
            let completeResponse: BlazeQueryResponse | null = null;

            // Create a reader from the response body stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = ""; // To accumulate text chunks

            // Add a safety timeout to prevent infinite hanging
            const streamTimeout = setTimeout(() => {
              console.warn("Stream timeout reached, forcing completion");

              // If we have any complete response, use it
              if (completeResponse) {
                if (streamCallbacks?.onComplete) {
                  streamCallbacks.onComplete(completeResponse);
                }

                resolve({
                  data: completeResponse,
                  metrics: {
                    response_time: performance.now() - startTime,
                  },
                });
              } else {
                // Create a fallback response if no data was received
                const timeoutFallback: BlazeQueryResponse = {
                  response_type: "academic_within_scope",
                  answer:
                    "I received your query but encountered a processing delay. Please try asking your question again.",
                  detected_language: "english",
                  metrics: {
                    execution_time: 0,
                    chatgpt_prompt: query,
                    chatgpt_model: "gemini-2.0-flash",
                    syllabus: syllabus,
                    subject_name: "General",
                    chapter_name: "General",
                    grade: grade,
                    language: "english",
                  },
                };

                if (streamCallbacks?.onComplete) {
                  streamCallbacks.onComplete(timeoutFallback);
                }

                resolve({
                  data: timeoutFallback,
                  metrics: {
                    response_time: performance.now() - startTime,
                  },
                });
              }

              // Cleanup
              reader.cancel();
            }, 30000); // 30 second timeout

            // Process the stream with an async function using while(true)
            (async function processStream() {
              try {
                let accumulatedText = "";
                let hasSeenContent = false;

                // Use while(true) with a break on done
                while (true) {
                  // Read the next chunk
                  const { done, value } = await reader.read();

                  console.log("Stream chunk received:", {
                    done,
                    valueLength: value?.length,
                  });

                  // Check if the stream is complete
                  if (done) {
                    // Clear the timeout since we completed normally
                    clearTimeout(streamTimeout);

                    console.log(
                      "Stream completed, accumulated text length:",
                      accumulatedText.length
                    );

                    // Try to parse accumulated text as JSON first (for buddy recommendations and structured responses)
                    let finalResponse: BlazeQueryResponse | null = null;

                    if (accumulatedText.trim()) {
                      try {
                        // Check if the accumulated text looks like JSON
                        const trimmedText = accumulatedText.trim();
                        if (
                          (trimmedText.startsWith("{") &&
                            trimmedText.endsWith("}")) ||
                          (trimmedText.startsWith("[") &&
                            trimmedText.endsWith("]"))
                        ) {
                          console.log(
                            "🔍 Attempting to parse response as JSON..."
                          );
                          const jsonResponse = JSON.parse(trimmedText);

                          // If it's a valid JSON response with response_type, use it directly
                          if (jsonResponse.response_type) {
                            console.log(
                              "✅ Successfully parsed JSON response:",
                              jsonResponse.response_type
                            );
                            finalResponse = jsonResponse;
                          }
                        }
                      } catch (jsonError) {
                        console.log(
                          "📝 Not valid JSON, treating as plain text"
                        );
                        // Not JSON, fall through to text handling
                      }

                      // If we didn't get a valid JSON response, create a text response
                      if (!finalResponse) {
                        console.log("📝 Creating text-based response");
                        finalResponse = {
                          response_type: "academic_within_scope",
                          answer: accumulatedText,
                          english_answer: accumulatedText,
                          detected_language: "english",
                          metrics: {
                            execution_time: 0,
                            chatgpt_prompt: query,
                            chatgpt_model: "gemini-2.0-flash",
                            syllabus: syllabus,
                            subject_name: "General",
                            chapter_name: "General",
                            grade: grade,
                            language: "english",
                          },
                        };
                      }
                    } else {
                      // No content received
                      console.log(
                        "⚠️ No content received, creating fallback response"
                      );
                      finalResponse = {
                        response_type: "academic_within_scope",
                        answer:
                          "I received your query but didn't get a complete response. Please try again.",
                        english_answer:
                          "I received your query but didn't get a complete response. Please try again.",
                        detected_language: "english",
                        metrics: {
                          execution_time: 0,
                          chatgpt_prompt: query,
                          chatgpt_model: "gemini-2.0-flash",
                          syllabus: syllabus,
                          subject_name: "General",
                          chapter_name: "General",
                          grade: grade,
                          language: "english",
                        },
                      };
                    }

                    // Store the complete response
                    completeResponse = finalResponse;

                    // Call completion callback
                    if (streamCallbacks?.onComplete) {
                      console.log(
                        "🎉 Calling onComplete with final response:",
                        finalResponse.response_type
                      );
                      streamCallbacks.onComplete(finalResponse);
                    }

                    // Resolve the promise
                    resolve({
                      data: finalResponse,
                      metrics: {
                        response_time: performance.now() - startTime,
                      },
                    });

                    break; // Exit the while loop
                  }

                  // Process the chunk
                  if (value) {
                    const textChunk = decoder.decode(value, { stream: true });
                    console.log(
                      "📦 Chunk received:",
                      textChunk.substring(0, 100) + "..."
                    );

                    // Add to accumulated text
                    accumulatedText += textChunk;

                    // Check if this looks like a JSON response (buddy recommendation)
                    const trimmedAccumulated = accumulatedText.trim();
                    if (
                      trimmedAccumulated.startsWith("{") &&
                      trimmedAccumulated.includes('"response_type"')
                    ) {
                      try {
                        // Try to parse as JSON to see if we have a complete JSON response
                        const jsonResponse = JSON.parse(trimmedAccumulated);

                        if (
                          jsonResponse.response_type === "buddy_recommendation"
                        ) {
                          console.log(
                            "🤖 Detected complete buddy recommendation JSON"
                          );

                          // For buddy recommendations, send the complete JSON immediately
                          if (streamCallbacks?.onChunkReceived) {
                            streamCallbacks.onChunkReceived(jsonResponse);
                          }

                          // Don't continue streaming for JSON responses - they're complete
                          continue;
                        } else if (jsonResponse.response_type) {
                          console.log(
                            "📋 Detected complete structured response:",
                            jsonResponse.response_type
                          );

                          // For other structured responses, send them immediately
                          if (streamCallbacks?.onChunkReceived) {
                            streamCallbacks.onChunkReceived(jsonResponse);
                          }

                          continue;
                        }
                      } catch (parseError) {
                        // Not complete JSON yet, continue accumulating
                        console.log("📝 JSON not complete yet, continuing...");
                      }
                    }

                    // For text responses, send chunk updates for real-time streaming display
                    if (
                      accumulatedText.trim() &&
                      !trimmedAccumulated.startsWith("{")
                    ) {
                      const chunkResponse = {
                        response_type: "academic_within_scope" as const,
                        answer: accumulatedText,
                        english_answer: accumulatedText,
                        detected_language: "english" as const,
                      };

                      if (streamCallbacks?.onChunkReceived) {
                        streamCallbacks.onChunkReceived(chunkResponse);
                      }
                    }

                    hasSeenContent = true;
                  }
                }
              } catch (error) {
                console.error("❌ Stream processing error:", error);

                // Clear timeout on error
                clearTimeout(streamTimeout);

                // Call error callback
                if (streamCallbacks?.onError) {
                  streamCallbacks.onError(error as Error);
                }

                reject(error);
              }
            })();
          })
          .catch((error) => {
            console.log("Error in stream: ", JSON.stringify(error.name));
            if (streamCallbacks?.onError) {
              streamCallbacks.onError(error);
            }
            reject(error);
          });
      }
    );
  };

  const _mutate = useMutation({
    mutationFn: fetchAnswerFromQuery,
    retry: false,
    ...(options ?? {}),
  });

  return _mutate;
};

type UseFeedbackMutateOptions = {
  reference: any;
  feedback: any;
};

export const useFeedbackMutate = (
  options?: MutateOptions<void, Error, UseFeedbackMutateOptions>
) => {
  const postFeedback: MutateFunction<
    void,
    Error,
    UseFeedbackMutateOptions
  > = async ({ reference, feedback }) => {
    require("../../../firebase-config").db.runTransaction(
      async (transaction) => {
        const chatRef = reference.collection("llm_meta").doc(reference.id);
        if (feedback) {
          transaction.set(chatRef, { feedback }, { merge: true });
        }
      }
    );
  };

  const _mutate = useMutation({
    mutationFn: postFeedback,
    retry: 1,
    ...(options ?? {}),
  });

  return _mutate;
};

type UseFeedbackQueryOptions = {
  reference: any;
  enabled: boolean;
};

export const useFeedbackQuery = (options?: UseFeedbackQueryOptions) => {
  const fetchFeedback: QueryFunction<any, any> = async ({ queryKey }) => {
    const [, referenceId] = queryKey;
    const feedbackRef = await options?.reference
      .collection("llm_meta")
      .doc(referenceId);
    const feedbackDoc = await feedbackRef.get();
    return feedbackDoc.data().feedback;
  };

  const _query = useQuery({
    queryKey: ["feedback", options?.reference?.id],
    queryFn: fetchFeedback,
    enabled: !!options?.reference?.id,
    ...(options ?? {}),
  });

  return _query;
};
